import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Checkbox,
  Select,
  Row,
  Col,
  Table,
  Space,
  Card,
  Typography,
  App,
} from "antd";
import {
  SearchOutlined,
  GlobalOutlined,
  SaveOutlined,
  CalendarOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { API_URL } from "../../shared/utils/Util";
import { getFetchT, fetchT } from "../../shared/utils/TypedUtil";
import { GetMenuResponse } from "../../types/api";
import { Campaign, ItemSchema, ItemModificationSchema } from "../../types/model";
import { useAntdTheme } from "../../shared/hooks";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface GroupMenuForm {
  name: string;
  slug: string;
  description: string;
  ticket_prefix: string;
  order_deadline_date: Dayjs;
  order_deadline_time: Dayjs;
  fulfillment_date_range?: [Dayjs, Dayjs];
  fulfillment_time_start?: Dayjs;
  fulfillment_time_end?: Dayjs;
  is_batch_delivery: boolean;
}

const CampaignEditor: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notification } = App.useApp();
  const restaurant_id = sessionStorage.getItem("restaurant_id");
  const location = useLocation();
  const [form] = Form.useForm<GroupMenuForm>();
  const copiedCampaign = (location.state as { campaign?: Campaign } | null)?.campaign;

  const [itemSchemas, setItemSchemas] = useState<ItemSchema[]>([]);
  const [modificationSchemas, setModificationSchemas] = useState<ItemModificationSchema[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [itemOverrides, setItemOverrides] = useState<{ [key: string]: string[] }>({});

  useEffect(() => {
    if (restaurant_id) {
      getFetchT<GetMenuResponse>(
        `${API_URL}/v1/r/${restaurant_id}/menu`,
        (data) => {
          const allItems = data.item_schema_groups.flatMap((g) => g.item_schemas);
          setItemSchemas(allItems);
        },
        (err) => {
          notification.error({
            message: "Failed to fetch item schemas",
            description: err.toString(),
          });
        }
      );

      getFetchT<{ schemas: ItemModificationSchema[] }>(
        `${API_URL}/v1/r/${restaurant_id}/item_modification_schemas`,
        (data) => {
          setModificationSchemas(data.schemas);
        },
        (err) => {
          notification.error({
            message: "Failed to fetch modification schemas",
            description: err.toString(),
          });
        }
      );
    }
  }, [restaurant_id]);

  useEffect(() => {
    if (!copiedCampaign) {
      return;
    }

    form.setFieldsValue({
      name: `${t("campaign_copy_prefix")}${copiedCampaign.name}`,
      slug: `${copiedCampaign.slug}-copy`,
      description: copiedCampaign.description ?? "",
      ticket_prefix: copiedCampaign.ticket_prefix,
      is_batch_delivery: copiedCampaign.is_batch_delivery,
    });
    setSelectedItems(copiedCampaign.group_items.map((item) => item.item_schema_id));
    setItemOverrides(
      Object.fromEntries(
        copiedCampaign.group_items
          .filter((item) => item.override?.modification_schema_ids?.length)
          .map((item) => [item.item_schema_id, item.override?.modification_schema_ids ?? []])
      )
    );
  }, [copiedCampaign, form, t]);

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : "",
  });

  if (!restaurant_id) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  const onFinish = (values: GroupMenuForm) => {
    const orderDeadline = values.order_deadline_date
      .hour(values.order_deadline_time.hour())
      .minute(values.order_deadline_time.minute())
      .second(0)
      .utc()
      .format();

    const fulfillmentTimeStartSecond = values.fulfillment_time_start
      ? values.fulfillment_time_start.hour() * 3600 + values.fulfillment_time_start.minute() * 60
      : undefined;

    const fulfillmentTimeEndSecond = values.fulfillment_time_end
      ? values.fulfillment_time_end.hour() * 3600 + values.fulfillment_time_end.minute() * 60
      : undefined;

    const groupMenu = {
      restaurant_id,
      name: values.name,
      slug: values.slug,
      description: values.description,
      ticket_prefix: values.ticket_prefix,
      order_deadline: orderDeadline,
      fulfillment_start_date: values.fulfillment_date_range?.[0]?.format("YYYY-MM-DD"),
      fulfillment_end_date: values.fulfillment_date_range?.[1]?.format("YYYY-MM-DD"),
      fulfillment_time_start_second: fulfillmentTimeStartSecond,
      fulfillment_time_end_second: fulfillmentTimeEndSecond,
      is_batch_delivery: values.is_batch_delivery,
      group_items: selectedItems.map((itemId) => {
        const override_modification_schema_ids = itemOverrides[itemId] || [];
        return {
          item_schema_id: itemId,
          override:
            override_modification_schema_ids.length > 0
              ? {
                  modification_schema_ids: override_modification_schema_ids,
                  modification_schemas: modificationSchemas.filter((ms) =>
                    override_modification_schema_ids.includes(ms.id)
                  ),
                }
              : undefined,
        };
      }),
    };

    fetchT(
      "POST",
      `${API_URL}/v1/r/${restaurant_id}/campaigns`,
      groupMenu,
      () => {
        notification.success({
          message: "Group Menu created successfully",
        });
        form.resetFields();
        setSelectedItems([]);
        setItemOverrides({});
      },
      (err) => {
        notification.error({
          message: "Failed to create Group Menu",
          description: err.toString(),
        });
      }
    );
  };

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Override Modification Schemas",
      key: "overrides",
      render: (text: any, record: ItemSchema) => {
        const isSelected = selectedItems.includes(record.id);
        return (
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Select modifications"
            disabled={!isSelected}
            value={itemOverrides[record.id] || []}
            onChange={(value) => {
              setItemOverrides((prev) => ({ ...prev, [record.id]: value }));
            }}
          >
            {modificationSchemas.map((schema) => (
              <Option key={schema.id} value={schema.id}>
                {schema.description}
              </Option>
            ))}
          </Select>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedItems,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedItems(selectedRowKeys.map((key) => String(key)));
    },
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        is_batch_delivery: false,
      }}
    >
      {/* Basic Information */}
      <Card
        title={
          <Space>
            <GlobalOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("campaign_basic_information")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Form.Item
          name="name"
          label={t("name")}
          rules={[{ required: true, message: t("please_input_name") }]}
        >
          <Input size="large" style={{ borderRadius: token.borderRadiusLG }} />
        </Form.Item>
        <Form.Item
          name="slug"
          label={t("slug")}
          rules={[{ required: true, message: t("please_input_slug") }]}
        >
          <Input size="large" style={{ borderRadius: token.borderRadiusLG }} />
        </Form.Item>
        <Form.Item name="description" label={t("description")}>
          <Input.TextArea rows={4} style={{ borderRadius: token.borderRadiusLG }} />
        </Form.Item>
        <Form.Item
          name="ticket_prefix"
          label={t("ticket_prefix")}
          rules={[{ required: true, message: t("please_input_ticket_prefix") }]}
        >
          <Input size="large" style={{ borderRadius: token.borderRadiusLG }} />
        </Form.Item>
      </Card>

      {/* Date and Time Settings */}
      <Card
        title={
          <Space>
            <CalendarOutlined style={{ fontSize: 20, color: token.colorInfo }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("schedule_settings")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="order_deadline_date"
              label={t("order_deadline_date")}
              rules={[{ required: true, message: t("please_select_date") }]}
            >
              <DatePicker size="large" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="order_deadline_time"
              label={t("order_deadline_time")}
              rules={[{ required: true, message: t("please_select_time") }]}
            >
              <TimePicker format="HH:mm" size="large" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Form.Item name="fulfillment_date_range" label={t("fulfillment_date_range")}>
              <RangePicker
                size="large"
                style={{
                  width: "100%",
                  borderRadius: token.borderRadiusLG,
                }}
                placeholder={[t("fulfillment_start_date"), t("fulfillment_end_date")]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item name="fulfillment_time_start" label={t("fulfillment_time_start")}>
              <TimePicker format="HH:mm" size="large" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="fulfillment_time_end" label={t("fulfillment_time_end")}>
              <TimePicker format="HH:mm" size="large" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="is_batch_delivery" valuePropName="checked">
          <Checkbox>{t("is_batch_delivery")}</Checkbox>
        </Form.Item>
      </Card>

      {/* Items Selection */}
      <Card
        title={
          <Space>
            <AppstoreOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("select_menu_items")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Form.Item label={t("items")}>
          <Table
            rowKey="id"
            rowSelection={rowSelection}
            dataSource={itemSchemas}
            columns={columns}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t("total_items_count", { total }),
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        </Form.Item>
      </Card>

      {/* Submit Button */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            size="large"
            style={{
              borderRadius: token.borderRadiusLG,
              height: token.controlHeightLG,
              minWidth: "200px",
            }}
          >
            {t("save_campaign")}
          </Button>
        </Form.Item>
      </Card>
    </Form>
  );
};

export default CampaignEditor;
